import { useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { sentenceCase } from "../../../utils/miscFunctions";

const AppearanceSettings = () => {
  const { theme, setTheme, resolvedTheme, density, setDensity, fontSize, setFontSize } = useTheme();
  
  const [appearanceData, setAppearanceData] = useState({
    theme: theme,
    language: 'en',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    density: density,
    fontSize: fontSize
  });

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setAppearanceData({ ...appearanceData, theme: newTheme });
  };

  const handleDensityChange = (newDensity: 'compact' | 'comfortable' | 'spacious') => {
    setDensity(newDensity);
    setAppearanceData({ ...appearanceData, density: newDensity });
  };

  const handleFontSizeChange = (newFontSize: 'small' | 'medium' | 'large') => {
    setFontSize(newFontSize);
    setAppearanceData({ ...appearanceData, fontSize: newFontSize });
  };
    
    return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-4">
          {(['light', 'dark', 'system'] as const).map((themeOption) => (
            <button
              key={themeOption}
              onClick={() => handleThemeChange(themeOption)}
              className={`p-4 border-2 rounded-lg text-center transition-colors ${
                theme === themeOption
                  ? 'border-gray-600 bg-gray-50 dark:border-gray-400 dark:bg-gray-800'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
            >
              <div className={`w-full h-16 rounded mb-2 ${
                themeOption === 'light' ? 'bg-white border dark:bg-white' :
                themeOption === 'dark' ? 'bg-gray-800 border dark:bg-black' :
                'bg-gradient-to-r from-white to-gray-800'
              }`} />
              <span className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">{themeOption}</span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Current active theme: <span className="font-medium">{sentenceCase(resolvedTheme)}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
          <select
            value={appearanceData.language}
            onChange={(e) => setAppearanceData({ ...appearanceData, language: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-800 dark:text-white"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
            <option value="zh">Chinese</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
          <select
            value={appearanceData.timezone}
            onChange={(e) => setAppearanceData({ ...appearanceData, timezone: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-800 dark:text-white"
          >
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
            <option value="Europe/London">London</option>
            <option value="Europe/Paris">Paris</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Format</label>
          <select
            value={appearanceData.dateFormat}
            onChange={(e) => setAppearanceData({ ...appearanceData, dateFormat: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-800 dark:text-white"
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time Format</label>
          <select
            value={appearanceData.timeFormat}
            onChange={(e) => setAppearanceData({ ...appearanceData, timeFormat: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-800 dark:text-white"
          >
            <option value="12h">12 Hour (AM/PM)</option>
            <option value="24h">24 Hour</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Interface Density</label>
        <div className="grid grid-cols-3 gap-4">
          {['compact', 'comfortable', 'spacious'].map((densityOption) => (
            <button
              key={densityOption}
              onClick={() => handleDensityChange(densityOption as 'compact' | 'comfortable' | 'spacious')}
              className={`p-3 border-2 rounded-lg text-center transition-colors ${
                density === densityOption
                  ? 'border-gray-600 bg-gray-50 dark:border-gray-400 dark:bg-gray-800'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
            >
              <span className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">{densityOption}</span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Controls sidebar expansion: <span className="font-medium">{density === 'compact' ? 'Collapsed' : density === 'spacious' ? 'Expanded' : 'Auto'}</span>
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Font Size</label>
        <div className="grid grid-cols-3 gap-4">
          {(['small', 'medium', 'large'] as const).map((fontSizeOption) => (
            <button
              key={fontSizeOption}
              onClick={() => handleFontSizeChange(fontSizeOption)}
              className={`p-3 border-2 rounded-lg text-center transition-colors ${
                fontSize === fontSizeOption
                  ? 'border-gray-600 bg-gray-50 dark:border-gray-400 dark:bg-gray-800'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              }`}
            >
              <span className={`font-medium capitalize text-gray-900 dark:text-gray-100 ${
                fontSizeOption === 'small' ? 'text-xs' : 
                fontSizeOption === 'medium' ? 'text-sm' : 
                'text-base'
              }`}>
                {fontSizeOption === 'small' ? 'Aa' : fontSizeOption === 'medium' ? 'Aa' : 'Aa'}
              </span>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 capitalize">
                {fontSizeOption}
              </div>
            </button>
          ))}
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Adjust text size for better readability: <span className="font-medium capitalize">{fontSize}</span>
        </p>
      </div>
    </div>
  )
};

export default AppearanceSettings;